/**
 * @file components/Navbar.js
 * @description Top navigation bar with dark mode toggle, Field Mode toggle,
 * user menu dropdown, and mobile hamburger menu.
 * Rebuilt with design system components.
 */
import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../hooks/useTheme";
import { useTheme as useDesignTheme } from "../design-system";
import { Button } from "../design-system";
// The re-sign-in card owns itself; the menu only knocks on its door.
import { openReSignIn } from "./ReSignIn";

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function FieldModeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
      <path d="M2 17l10 5 10-5"/>
      <path d="M2 12l10 5 10-5"/>
    </svg>
  );
}

function Navbar() {
  const auth = useAuth();
  const { theme } = useTheme();
  const designTheme = useDesignTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // The suite hub (/apps) is BUTLER CORE's page — no module branding or menu
  // there (SITE-MAP.md rule #1/#2). Modules get their nav back on their pages.
  const onHub = location.pathname === "/";

  // Logout lands on the Butler Core sign-in (SITE-MAP), not the legacy '/app/'
  // root from the pre-front-door era. Full reload wipes in-memory state.
  const handleLogout = async () => { try { await auth.logout(); } catch (_) {} window.location.href = "/app/login"; };

  useEffect(() => { setMenuOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [userMenuOpen]);

  if (!auth.isAuthenticated) return null;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  // Mirrors the server-side HR page gate (HR_ROLES) so the nav only offers
  // the door to people it will actually open for.
  const HR_NAV_ROLES = ["admin", "company_admin", "super_admin"];

  const links = [
    { to: "/", label: "⊞ Apps" },
    { to: "https://butlercore.com/report-engine/editor", label: "Inspections", external: true },
    { to: "https://butlercore.com/oncall/", label: "On-Call", external: true },
    { to: "/well", label: "Well & Septic" },
    // "Team" — the HR module's People page (master roster). /hr is a server-
    // rendered page OUTSIDE the SPA, so it must be a plain full-page anchor
    // (external: true), never a React route. Shown only to the roles the HR
    // uiGate opens for — mirrors HR_ROLES in server/src/modules/hr/routes.js.
    ...(HR_NAV_ROLES.includes(auth.user?.role) ? [{ to: "/hr", label: "Team", external: true }] : []),
    { to: "/settings", label: "Settings" }
  ];

  const isDark = designTheme.resolvedTheme === "dark";
  const isFieldMode = designTheme.fieldMode;
  const userInitials = (auth.user?.firstName?.[0] || "") + (auth.user?.lastName?.[0] || "");

  const toggleDarkMode = () => {
    designTheme.setTheme(isDark ? "light" : "dark");
  };

  const toggleFieldMode = () => {
    designTheme.toggleFieldMode();
  };

  return (
    <>
      <div className="navbar" style={{
        backgroundColor: "var(--surface-raised)",
        borderColor: "var(--border-default)",
        fontFamily: "var(--font-family)",
      }}>
        <Link to="/" className="navbar-logo" style={{ color: "var(--text-primary)", fontFamily: "var(--font-family)" }}>
          <svg width="24" height="24" viewBox="0 0 48 48" fill="none" style={{ marginRight: "var(--space-2)" }}>
            <rect width="48" height="48" rx="10" fill="var(--color-primary)" />
            <path d="M16 18h16M16 24h16M16 30h10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M30 28l3 3 5-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {/* SITE-MAP rule #1: the hub is BUTLER CORE — no module's name on the
              front door. Module pages show YOUR COMPANY's name: an explicitly
              customized theme name wins; otherwise the company on the logged-in
              account (e.g. WNCIL); the old "Inspection Butler" factory default
              is treated as unset so it stops photobombing other modules. */}
          {onHub
            ? "Butler Core"
            : ((theme.companyName && theme.companyName !== "Inspection Butler")
                ? theme.companyName
                : (auth.user?.companyName || auth.user?.company?.name || "Butler Suite"))}
        </Link>

        <div className="navbar-nav navbar-desktop" style={{ gap: "var(--space-0_5)" }}>
          {!onHub && links.map(l => {
            const linkStyle = {
              color: isActive(l.to) ? "var(--text-primary)" : "var(--text-secondary)",
              fontFamily: "var(--font-family)",
              fontWeight: isActive(l.to) ? "var(--weight-semibold)" : "var(--weight-medium)",
              fontSize: "var(--text-body-sm)",
              borderRadius: "var(--radius-md)",
              backgroundColor: isActive(l.to) ? "var(--overlay-light)" : "transparent",
            };
            const className = `nav-link ${isActive(l.to) ? "active" : ""}`;
            return l.external ? (
              <a key={l.to} href={l.to} className={className} style={linkStyle}>
                {l.label}
              </a>
            ) : (
              <Link key={l.to} to={l.to} className={className} style={linkStyle}>
                {l.label}
              </Link>
            );
          })}

          {/* Dark mode toggle */}
          <button onClick={toggleDarkMode} title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: "var(--radius-md)",
              border: "none", backgroundColor: "transparent", color: "var(--text-secondary)",
              cursor: "pointer", marginLeft: "var(--space-2)",
            }}>
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Field mode toggle */}
          <button onClick={toggleFieldMode} title={isFieldMode ? "Disable Field Mode" : "Enable Field Mode"}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: "var(--radius-md)",
              border: isFieldMode ? "2px solid var(--color-primary)" : "none",
              backgroundColor: isFieldMode ? "var(--color-primary-subtle)" : "transparent",
              color: isFieldMode ? "var(--color-primary)" : "var(--text-secondary)",
              cursor: "pointer",
            }}>
            <FieldModeIcon />
          </button>

          {/* User menu */}
          <div ref={userMenuRef} style={{ position: "relative", marginLeft: "var(--space-2)" }}>
            <button onClick={() => setUserMenuOpen(!userMenuOpen)}
              style={{
                width: 36, height: 36, borderRadius: "var(--radius-full)",
                backgroundColor: "var(--color-primary)", color: "#fff",
                border: "none", cursor: "pointer", fontWeight: "var(--weight-bold)",
                fontSize: "var(--text-caption)", fontFamily: "var(--font-family)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
              {userInitials || "U"}
            </button>
            {userMenuOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + var(--space-1))", right: 0,
                minWidth: "200px", backgroundColor: "var(--surface-raised)",
                border: "1px solid var(--border-default)", borderRadius: "var(--radius-lg)",
                boxShadow: "var(--shadow-lg)", zIndex: "var(--z-dropdown)",
                overflow: "hidden",
              }}>
                <div style={{ padding: "var(--space-3) var(--space-4)", borderBottom: "1px solid var(--border-default)" }}>
                  <div style={{ fontWeight: "var(--weight-semibold)", color: "var(--text-primary)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-family)" }}>{auth.user?.firstName} {auth.user?.lastName}</div>
                  <div style={{ color: "var(--text-tertiary)", fontSize: "var(--text-caption)", fontFamily: "var(--font-family)" }}>{auth.user?.email}</div>
                </div>
                <div style={{ padding: "var(--space-1)" }}>
                  {/* The same door the orange "your sign-in ends soon" bar
                      offers, available ANY time — so a long field day can be
                      started on a fresh 24 hours instead of hitting the
                      deadline halfway through. It asks for the password every
                      time; nothing renews on its own. */}
                  <button onClick={() => { setUserMenuOpen(false); openReSignIn(); }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "var(--space-2) var(--space-3)", border: "none", backgroundColor: "transparent", color: "var(--text-primary)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-family)", cursor: "pointer", borderRadius: "var(--radius-sm)" }}>
                    Sign in again (fresh 24 hours)
                  </button>
                  <button onClick={() => { navigate("/settings"); setUserMenuOpen(false); }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "var(--space-2) var(--space-3)", border: "none", backgroundColor: "transparent", color: "var(--text-primary)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-family)", cursor: "pointer", borderRadius: "var(--radius-sm)" }}>
                    Settings
                  </button>
                  <button onClick={handleLogout}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "var(--space-2) var(--space-3)", border: "none", backgroundColor: "transparent", color: "var(--color-error)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-family)", cursor: "pointer", borderRadius: "var(--radius-sm)" }}>
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <button className="navbar-hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" style={{ color: "var(--text-primary)" }}>
          <span className={`hamburger-line ${menuOpen ? "open" : ""}`} />
          <span className={`hamburger-line ${menuOpen ? "open" : ""}`} />
          <span className={`hamburger-line ${menuOpen ? "open" : ""}`} />
        </button>
      </div>

      {menuOpen && (
        <div className="navbar-mobile-overlay" onClick={() => setMenuOpen(false)}>
          <div className="navbar-mobile-menu" onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "var(--surface-raised)" }}>
            <div className="mobile-menu-header" style={{ borderColor: "var(--border-default)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-3)" }}>
                <div style={{ width: 40, height: 40, borderRadius: "var(--radius-full)", backgroundColor: "var(--color-primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-bold)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-family)" }}>{userInitials || "U"}</div>
                <div>
                  <span style={{ fontWeight: "var(--weight-semibold)", color: "var(--text-primary)", display: "block", fontFamily: "var(--font-family)" }}>{auth.user?.firstName} {auth.user?.lastName}</span>
                  <span style={{ fontSize: "var(--text-caption)", color: "var(--text-secondary)", fontFamily: "var(--font-family)" }}>{auth.user?.email}</span>
                </div>
              </div>
              {/* Mobile toggles */}
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <button onClick={toggleDarkMode} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--space-2)", padding: "var(--space-2)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-default)", backgroundColor: isDark ? "var(--color-primary-subtle)" : "transparent", color: isDark ? "var(--color-primary)" : "var(--text-secondary)", cursor: "pointer", fontSize: "var(--text-caption)", fontFamily: "var(--font-family)", fontWeight: "var(--weight-medium)" }}>
                  {isDark ? <SunIcon /> : <MoonIcon />}
                  {isDark ? "Light" : "Dark"}
                </button>
                <button onClick={toggleFieldMode} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--space-2)", padding: "var(--space-2)", borderRadius: "var(--radius-md)", border: isFieldMode ? "2px solid var(--color-primary)" : "1px solid var(--border-default)", backgroundColor: isFieldMode ? "var(--color-primary-subtle)" : "transparent", color: isFieldMode ? "var(--color-primary)" : "var(--text-secondary)", cursor: "pointer", fontSize: "var(--text-caption)", fontFamily: "var(--font-family)", fontWeight: "var(--weight-medium)" }}>
                  <FieldModeIcon />
                  Field
                </button>
              </div>
            </div>
            <nav className="mobile-menu-nav">
              {(onHub ? links.filter(l => l.to === "/") : links).map(l => {
                const mobileClass = `mobile-nav-link ${isActive(l.to) ? "active" : ""}`;
                const mobileStyle = { color: "var(--text-primary)", fontFamily: "var(--font-family)" };
                return l.external ? (
                  <a key={l.to} href={l.to} className={mobileClass} style={mobileStyle}>
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.to} to={l.to} className={mobileClass} style={mobileStyle}>
                    {l.label}
                  </Link>
                );
              })}
            </nav>
            <div className="mobile-menu-footer" style={{ borderColor: "var(--border-default)" }}>
              <button onClick={() => { setMenuOpen(false); openReSignIn(); }} className="mobile-nav-link"
                style={{ color: "var(--text-primary)", fontFamily: "var(--font-family)", border: "none", background: "none", width: "100%", textAlign: "left", cursor: "pointer" }}>
                Sign in again (fresh 24 hours)
              </button>
              <button onClick={handleLogout} className="mobile-nav-link" style={{ color: "var(--color-error)", fontFamily: "var(--font-family)", border: "none", background: "none", width: "100%", textAlign: "left", cursor: "pointer" }}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
